import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import './ProductDetails.css';
import useAddToCart from './AddToCartButton';
import { useCart } from './CartContext';
import BASE_URL from '../../config';

const ProductDetailDe = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { fetchCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState(null);
  const [buyNowPending, setBuyNowPending] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const {
    handleAddToCart,
    loading: addToCartLoading,
    error: addToCartError,
    success: addToCartSuccess,
    loginMessage,
  } = useAddToCart();

  const formatPreparationTime = (time) => {
    const [days, timeStr] = time.split(" ");
    const [hours] = timeStr.split(":");
    return `${days} Tag${days > 1 ? "e" : ""} ${hours} Stunde${hours > 1 ? "n" : ""}`;
  };

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/product/${slug}/?format=json`)
      .then(response => {
        setProduct(response.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err);
        setLoading(false);
      });

    const anonymousUserID = localStorage.getItem('anonymousUserID');
    if (!anonymousUserID) {
      axios
        .get(`${BASE_URL}/api/anonymous-user/`)
        .then(response => {
          localStorage.setItem('anonymousUserID', response.data.anonymous_user_id);
        })
        .catch(err => console.error('Error fetching anonymous user ID:', err));
    }
  }, [slug]);

  // React to success / error states from useAddToCart
  useEffect(() => {
    if (addToCartSuccess) {
      if (buyNowPending) {
        setBuyNowPending(false);
        navigate('/checkout');
      } else {
        setMessage({
          type: 'success',
          text: `✓ ${quantity} × ${product?.title || 'Artikel'} wurde zum Warenkorb hinzugefügt.`,
          showCartLink: true,
        });
      }
    }
  }, [addToCartSuccess]);

  useEffect(() => {
    if (addToCartError) {
      setBuyNowPending(false);
      setMessage({ type: 'error', text: addToCartError });
    }
  }, [addToCartError]);

  // Auto-hide messages after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const onAddToCartClick = () => {
    setBuyNowPending(false);
    handleAddToCart(product.slug, quantity, fetchCart);
  };

  const onBuyNowClick = () => {
    setBuyNowPending(true);
    handleAddToCart(product.slug, quantity, fetchCart);
  };

  const increaseQuantity = () => {
    const maxStock = product?.stock;
    setQuantity(q => {
      if (maxStock !== null && maxStock !== undefined && q >= maxStock) return q;
      return q + 1;
    });
  };
  const decreaseQuantity = () => setQuantity(q => Math.max(1, q - 1));

  const isOutOfStock = product && product.stock !== null && product.stock !== undefined && product.stock <= 0;
  const isLowStock = product && product.stock !== null && product.stock !== undefined && product.stock > 0 && product.stock <= 5;

  if (loading)
    return (
      <div className="pdp" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner-border text-secondary" role="status" />
      </div>
    );
  if (error) return <p className="pdp">Fehler: {error.message}</p>;
  if (!product) return null;

  return (
    <div className="pdp">
      {/* Breadcrumb */}
      <nav className="pdp-breadcrumb">
        <a href="/">Heim</a>
        <span className="separator">/</span>
        <a href="/products">Produkte</a>
        <span className="separator">/</span>
        <span className="current">{product.title}</span>
      </nav>

      <div className="pdp-grid">
        {/* Image Gallery */}
        <div className="pdp-image-col">
          {(() => {
            const allImages = [];
            if (product.mainimage) allImages.push(product.mainimage);
            if (product.images?.length) {
              product.images.forEach(img => {
                if (img.image && img.image !== product.mainimage) allImages.push(img.image);
              });
            }
            if (allImages.length === 0) allImages.push('default_image_path.jpg');

            return (
              <>
                <div className="pdp-carousel">
                  <img
                    src={allImages[currentImageIndex]}
                    alt={`${product.title} - ${currentImageIndex + 1}`}
                    className="pdp-image"
                  />
                  {allImages.length > 1 && (
                    <>
                      <button
                        className="pdp-arrow pdp-arrow-left"
                        onClick={() => setCurrentImageIndex(i => i === 0 ? allImages.length - 1 : i - 1)}
                        aria-label="Vorheriges Bild"
                      >
                        ‹
                      </button>
                      <button
                        className="pdp-arrow pdp-arrow-right"
                        onClick={() => setCurrentImageIndex(i => i === allImages.length - 1 ? 0 : i + 1)}
                        aria-label="Nächstes Bild"
                      >
                        ›
                      </button>
                      <div className="pdp-image-counter">
                        {currentImageIndex + 1} / {allImages.length}
                      </div>
                    </>
                  )}
                </div>
                {allImages.length > 1 && (
                  <div className="pdp-thumbnails">
                    {allImages.map((img, idx) => (
                      <button
                        key={idx}
                        className={`pdp-thumb ${idx === currentImageIndex ? 'active' : ''}`}
                        onClick={() => setCurrentImageIndex(idx)}
                      >
                        <img src={img} alt={`${product.title} Miniatur ${idx + 1}`} />
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Info */}
        <div className="pdp-info">
          <span className="pdp-category">{product.category.name}</span>
          <h1 className="pdp-title">{product.title}</h1>

          {product.label !== 'Service' && (
            <>
              <div className="pdp-price">
                CHF {product.discount_price || product.price}
              </div>
              <span className="pdp-tax">Inkl. MwSt.</span>
              {isOutOfStock && (
                <div className="pdp-stock-badge out-of-stock">Ausverkauft</div>
              )}
              {isLowStock && (
                <div className="pdp-stock-badge low-stock">Nur noch {product.stock} auf Lager</div>
              )}
            </>
          )}

          {product.preparationtime && (
            <div className="pdp-prep">
              <strong>Vorbereitungszeit:</strong> {formatPreparationTime(product.preparationtime)}
            </div>
          )}

          {product.shortdescription && (
            <p className="pdp-short-desc">{product.shortdescription}</p>
          )}

          <hr className="pdp-divider" />

          {product.label === 'Service' ? (
            <>
              {/* Service item — Barista buchen */}
              <p className="pdp-service-note">Dies ist eine Dienstleistung — der Preis hängt von Ihren Eventdetails ab.</p>
              <Link to="/event-booking" className="pdp-book-btn">
                Barista buchen
              </Link>
              <p className="pdp-buy-hint">Füllen Sie das Formular aus und wir senden Ihnen ein Angebot</p>
            </>
          ) : (
            <>
              {/* Messages — shown above actions for visibility */}
              {loginMessage && (
                <div className="pdp-message error">{loginMessage}</div>
              )}
              {message && (
                <div className={`pdp-message ${message.type}`}>
                  {message.text}
                  {message.showCartLink && (
                    <Link to="/cart" className="pdp-cart-link">Warenkorb ansehen →</Link>
                  )}
                </div>
              )}

              {/* Quantity + Add to cart */}
              <div className="pdp-actions">
                <div className="pdp-qty">
                  <button onClick={decreaseQuantity} disabled={addToCartLoading || isOutOfStock}>−</button>
                  <input type="number" value={quantity} readOnly />
                  <button onClick={increaseQuantity} disabled={addToCartLoading || isOutOfStock}>+</button>
                </div>
                <button
                  className="pdp-add-btn"
                  onClick={onAddToCartClick}
                  disabled={addToCartLoading || isOutOfStock}
                >
                  {isOutOfStock ? 'Ausverkauft' : addToCartLoading ? 'Wird hinzugefügt…' : 'In den Warenkorb'}
                </button>
              </div>

              {/* Buy now — secondary action: skip cart, go to checkout */}
              <button
                className="pdp-buy-btn"
                onClick={onBuyNowClick}
                disabled={addToCartLoading || isOutOfStock}
              >
                {isOutOfStock ? 'Nicht verfügbar' : addToCartLoading ? 'Wird verarbeitet…' : 'Sofort kaufen'}
              </button>
              <p className="pdp-buy-hint">Überspringt den Warenkorb – direkt zur Kasse</p>
            </>
          )}

          <Link to="/products" className="pdp-continue">← Weiter einkaufen</Link>

          {/* Description */}
          {product.description && (
            <div className="pdp-section">
              <h3 className="pdp-section-title">Produktbeschreibung</h3>
              <p className="pdp-section-text">{product.description}</p>
            </div>
          )}

          {/* Product info */}
          {product.productinfo && (
            <div className="pdp-section">
              <h3 className="pdp-section-title">Produktinformationen</h3>
              <p className="pdp-section-text">{product.productinfo}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailDe;
